import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WarningIcon from "@mui/icons-material/Warning";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import {SvgIconComponent} from "@mui/icons-material";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AirportShuttleIcon from "@mui/icons-material/AirportShuttle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";

export const engineTypes = [
    {id: 0, title: "Дизельный"},
    {id: 1, title: "Бензиновый"},
    {id: 4, title: "Электрический"},
    {id: 5, title: "Гибридный"},
];

export const tractionTypes = [
    {id: 0, title: "Колёсный"},
    {id: 1, title: "Гусеничный"},
    {id: 4, title: "Стационарный"},
    {id: 5, title: "Шагающий"},
];

export const transmissionTypes = [
    {id: 0, title: "Механическая"},
    {id: 1, title: "Гидростатическая"},
];

export const operatingTypes = [
    {id: 0, title: "Час"},
    {id: 1, title: "Километр"},
];

export interface IMachineryType {
    id: number;
    title: string;
}

export const machineryTypes: IMachineryType[] = [
    {id: 0, title: "Легковые а/м"},
    {id: 1, title: "Лёгкая коммерческая"},
    {id: 2, title: "Грузовые а/м"},
    {id: 3, title: "Экскаватор"},
    {id: 7, title: "Бульдозер"},
    {id: 18, title: "Комбайн сельскохозяйственный"},
    {id: 19, title: "Кран самоходный"},
    {id: 21, title: "Погрузчик"},
    {id: 28, title: "Самосвал"},
    {id: 34, title: "Трактор"},
    {id: 99, title: "Генератор электрический"},
    {id: 100, title: "Стационарная"},
    {id: 10000, title: "Другое"},
];

/**
 * Стационарная техника (ДЭС): 99 «Генератор электрический» и 100
 * «Стационарная». Пробег у неё не меняется и как показатель бессмыслен — в
 * интерфейсах вместо него показывается наработка в моточасах.
 *
 * Ровно то же множество лежит в STATIONARY_TYPE_IDS на бэкенде
 * (src/machinery_telemetry/sub-services/fleet-summary.service.ts), где по нему
 * глушатся пробего-зависимые метрики парка — при правке синхронизировать обе
 * стороны. Единственный источник этих литералов на фронте.
 */
export const STATIONARY_MACHINERY_TYPE_IDS: readonly number[] = [99, 100];

/** Стационарная ли машина этого типа. Проверять принадлежность только так. */
export const isStationaryMachineryType = (typeId?: number): boolean =>
    typeId != null && STATIONARY_MACHINERY_TYPE_IDS.includes(typeId);

/**
 * Синхронизировано с FILTER.ENGINE_RPM_RUNNING бэкенда; порог «двигатель
 * работает»: обороты выше него означают, что двигатель заведён.
 */
export const ENGINE_RPM_RUNNING = 300;

/** Заголовок типа по id (с fallback на «Другое»). */
export function getMachineryTypeTitle(typeId: number | null | undefined): string {
    return machineryTypes.find((t) => t.id === typeId)?.title ?? "Другое";
}

export const PRIORITIES = [
    {id: 1, title: "Ждёт", icon: AccessTimeIcon, color: "primary"},
    {id: 2, title: "Важно", icon: NewReleasesIcon, color: "warning"},
    {id: 3, title: "Срочно", icon: WarningIcon, color: "error"},
    //   {id: 4, title: "Срочно и важно", icon: WarningIcon, color: "error"},
];

export const problemCategories = [
    {id: 1, title: "Силовая установка"},
    {id: 2, title: "Система трансмиссии"},
    {id: 3, title: "Подвеска и ходовая часть"},
    {id: 4, title: "Тормозная система"},
    {id: 5, title: "Рулевое управление"},
    {id: 6, title: "Электрооборудование"},
    {id: 7, title: "Климатическая система"},
    {id: 8, title: "Система безопасности"},
    {id: 9, title: "Кузов и внешний вид"},
    {id: 10, title: "Вспомогательные и дополнительные системы"},
    {id: 11, title: "Системы управления двигателем"},
    {id: 12, title: "Рабочее оборудование"},
];

export const yearsManufacture = [
    "1990",
    "1991",
    "1992",
    "1993",
    "1994",
    "1995",
    "1996",
    "1997",
    "1998",
    "1999",
    "2000",
    "2001",
    "2002",
    "2003",
    "2004",
    "2005",
    "2006",
    "2007",
    "2008",
    "2009",
    "2010",
    "2011",
    "2012",
    "2013",
    "2014",
    "2015",
    "2016",
    "2017",
    "2018",
    "2019",
    "2020",
    "2021",
    "2022",
    "2023",
    "2024",
    "2025",
    "2026",
];

export enum MachineryStatus {
    active = "Работает",
    repair = "В ремонте",
    disActive = "Списана",
}

// ─── Аварийный маяк (Вега MX) ─────────────────────────────────
/**
 * Порог молчания основного трекера, после которого карта показывает позицию
 * машины по аварийному маяку. НЕ привязан к OFFLINE_THRESHOLD_STATIONARY_MS
 * (порог «нет связи» стационарной техники) — совпадение числа случайно.
 */
export const MAIN_SILENT_FOR_BEACON_MS = 2 * 60 * 60 * 1000;

/**
 * Маяк считается живым, если его последняя точка моложе этого числа часов
 * (суточный период пробуждения + люфт). beacon_location == null — это
 * «данных ещё не было», не «мёртв»: маяк мог быть привязан минуту назад.
 */
export const BEACON_ALIVE_HOURS = 26;

/**
 * История: хвостовая beacon-точка (позже последней точки основного трекера)
 * показывается маркером, только если удалена от неё дальше этого порога —
 * ближе это GPS-дрейф на стоянке, а не самостоятельная позиция.
 */
export const BEACON_TAIL_MIN_DISTANCE_M = 300;
